import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import type { IMessage } from '@/types';

/**
 * Messages Store
 *
 * This is NOT a typical query - it never fetches from server.
 * Messages are populated by:
 * - useLoadMessages (historical messages via SSE when cache is empty)
 * - useGenerateResponse (AI response when messages already cached - performance optimization)
 * - useSendMessage (optimistic user message)
 * - useStreamingMessage (AI response on stream_end)
 *
 * This "query" provides reactive access to the cached messages.
 */
export const useMessagesStore = (conversationId: string | null) => {
	return useQuery({
		queryKey: queryKeys.messages.byConversation(conversationId!),
		queryFn: async (): Promise<IMessage[]> => [],
		enabled: false, // Never fetch — data comes only via setQueryData from mutations/streaming
		staleTime: Infinity,
		gcTime: 10 * 60 * 1000 // Keep in cache for 10 minutes
	});
};

/**
 * Hook to manipulate messages cache
 * Used by streaming handler and mutations
 */
export const useMessagesCache = () => {
	const queryClient = useQueryClient();

	/**
	 * Append a message to the conversation's message list
	 */
	const appendMessage = useCallback(
		(conversationId: string, message: IMessage) => {
			queryClient.setQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId), (old = []) => [
				...old,
				message
			]);
		},
		[queryClient]
	);

	/**
	 * Set all messages for a conversation
	 * Used when loading existing messages
	 */
	const setMessages = useCallback(
		(conversationId: string, messages: IMessage[]) => {
			queryClient.setQueryData(queryKeys.messages.byConversation(conversationId), messages);
		},
		[queryClient]
	);

	/**
	 * Clear messages for a conversation
	 */
	const clearMessages = useCallback(
		(conversationId: string) => {
			queryClient.setQueryData(queryKeys.messages.byConversation(conversationId), []);
		},
		[queryClient]
	);

	/**
	 * Check if messages are already loaded for a conversation
	 */
	const hasMessages = useCallback(
		(conversationId: string): boolean => {
			const data = queryClient.getQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId));
			return Array.isArray(data) && data.length > 0;
		},
		[queryClient]
	);

	/**
	 * Get current messages from cache synchronously
	 * Used by edit/regenerate mutations to compute optimistic updates
	 */
	const getMessages = useCallback(
		(conversationId: string): IMessage[] => {
			return queryClient.getQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId)) || [];
		},
		[queryClient]
	);

	/**
	 * Patch a single message in the cache (merge fields without replacing the whole array).
	 * Used to sync lazy-loaded details (agentActivities, citations, tokenUsage) back into the
	 * main messages cache so parent components can access them reactively.
	 */
	const updateMessage = useCallback(
		(conversationId: string, messageId: string, patch: Partial<IMessage>) => {
			queryClient.setQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId), (old = []) => {
				if (!old.some((m) => m.id === messageId)) return old;
				return old.map((m) => (m.id === messageId ? { ...m, ...patch } : m));
			});
		},
		[queryClient]
	);

	return { appendMessage, setMessages, clearMessages, hasMessages, getMessages, updateMessage };
};
